import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { FileUpload } from '@/components/ui/FileUpload';
import { COMMON_TIMEZONE_OPTIONS } from '@/lib/timezone';
import type { DatasetFormData } from '@/components/datasets/useDatasetForm';
import styles from './DatasetForm.module.css';

interface DatasetFormProps {
  formData: DatasetFormData;
  error: string | null;
  setField: (field: keyof Omit<DatasetFormData, 'candlesFile' | 'newsFile'>, value: string) => void;
  setFile: (field: 'candlesFile' | 'newsFile', file: File | null) => void;
  isEdit: boolean;
}

export function DatasetForm({ formData, error, setField, setFile, isEdit }: DatasetFormProps) {
  const filesProvided = !!(formData.candlesFile && formData.newsFile);

  return (
    <div className={styles.form}>
      <div className={styles.metadataFields}>
        <Input
          label="Name"
          value={formData.displayName}
          onChange={(e) => setField('displayName', e.target.value)}
          placeholder="XAU Apr-May"
          required={!isEdit}
        />
        <Input
          label="Instrument"
          value={formData.instrument}
          onChange={(e) => setField('instrument', e.target.value)}
          required
        />
        <Select
          label="Candle Timezone"
          value={formData.sourceTimezone}
          onChange={(e) => setField('sourceTimezone', e.target.value)}
          options={COMMON_TIMEZONE_OPTIONS}
          required
        />
      </div>

      <p className="statusText statusTextMuted m-0">Execution metadata is optional for storage and required for new backtests. Use the broker's exact symbol specification.</p>
      <div className={styles.metadataFields}>
        <Input label="Volume unit" value={formData.volumeUnit} onChange={(e) => setField('volumeUnit', e.target.value)} placeholder="lots" />
        <Input label="Tick size" type="number" value={formData.tickSize} onChange={(e) => setField('tickSize', e.target.value)} />
        <Input label="Tick value" type="number" value={formData.tickValue} onChange={(e) => setField('tickValue', e.target.value)} />
        <Input label="Contract size" type="number" value={formData.contractSize} onChange={(e) => setField('contractSize', e.target.value)} />
        <Input label="Minimum volume" type="number" value={formData.minimumVolume} onChange={(e) => setField('minimumVolume', e.target.value)} />
        <Input label="Maximum volume" type="number" value={formData.maximumVolume} onChange={(e) => setField('maximumVolume', e.target.value)} />
        <Input label="Volume step" type="number" value={formData.volumeStep} onChange={(e) => setField('volumeStep', e.target.value)} />
        <Input label="Spread" type="number" value={formData.spread} onChange={(e) => setField('spread', e.target.value)} />
      </div>

      <div className={styles.fileFields}>
        <FileUpload
          label="1m Candles CSV"
          accept=".csv,text/csv"
          value={formData.candlesFile}
          onChange={(f) => setFile('candlesFile', f)}
          required={!isEdit}
        />
        <FileUpload
          label="News Events CSV"
          accept=".csv,text/csv"
          value={formData.newsFile}
          onChange={(f) => setFile('newsFile', f)}
          required={!isEdit}
        />
      </div>

      {isEdit && filesProvided && (
        <p className="statusText statusTextInfo m-0">
          Both CSV files selected — saving will replace the dataset data.
        </p>
      )}
      {isEdit && !filesProvided && (
        <p className="statusText statusTextMuted m-0">
          Leave both file fields empty to update metadata only.
        </p>
      )}

      {error && (
        <p className="statusText statusTextDanger m-0" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
